import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import { Button } from '../../components/buttons/Button';
import { Card } from '../../components/cards/Card';
import { Mail, Lock, AlertCircle, Loader2, CheckCircle } from 'lucide-react';

export const Login: React.FC = () => {
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();

  // Mode: 'login' or 'forgot'
  const [mode, setMode] = useState<'login' | 'forgot'>('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error: authError } = await login(email, password);

      if (authError) {
        setError(authError.message || 'Failed to sign in.');
        setIsLoading(false);
        return;
      }

      navigate('/');

    } catch (err) {
      setError('An unexpected error occurred.');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error: resetError } = await resetPassword(email);
      if (resetError) {
        setError(resetError.message || 'Failed to send reset email.');
      } else {
        setResetSent(true);
      }
    } catch (err) {
      setError('An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">

      {/* 1. Top Navigation Bar (Minimal) */}
      <nav className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-[#2f5c96] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">I</span>
            </div>
            <span className="font-bold text-xl text-[#2f5c96] tracking-tight">Indigo Beans Rooms</span>
          </Link>
          <div className="text-sm">
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold text-[#2f5c96] hover:text-[#c19cca] transition-colors">
              Sign up
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">

        {/* 2. Page Header */}
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center mb-8">
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {mode === 'login' ? 'Welcome back' : 'Reset Password'}
          </h2>
          <p className="mt-2 text-gray-500">
            {mode === 'login'
              ? 'Please enter your details to sign in.'
              : 'Enter your email to receive a reset link.'}
          </p>
        </div>

        {/* 3. Login Form Container */}
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Card className="py-8 px-4 sm:px-10 shadow-lg border-t-4 border-t-[#2f5c96]">

            {/* FORGOT PASSWORD SUCCESS STATE */}
            {mode === 'forgot' && resetSent ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Check your email</h3>
                <p className="text-gray-600 mb-6">
                  We've sent a password reset link to <strong>{email}</strong>.
                </p>
                <Button onClick={() => { setMode('login'); setResetSent(false); }} className="w-full">
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <>
                <form className="space-y-6" onSubmit={mode === 'login' ? handleLogin : handleForgotPassword}>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent sm:text-sm transition-all"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  {mode === 'login' && (
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="password"
                          name="password"
                          type="password"
                          autoComplete="current-password"
                          required
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent sm:text-sm transition-all"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    {mode === 'login' && (
                      <div className="flex items-center">
                        <input
                          id="remember-me"
                          name="remember-me"
                          type="checkbox"
                          checked={rememberMe}
                          onChange={e => setRememberMe(e.target.checked)}
                          className="h-4 w-4 text-[#2f5c96] focus:ring-[#2f5c96] border-gray-300 rounded cursor-pointer"
                        />
                        <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-600 cursor-pointer select-none">
                          Remember me
                        </label>
                      </div>
                    )}

                    <div className="text-sm ml-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setMode(mode === 'login' ? 'forgot' : 'login');
                          setError(null);
                        }}
                        className="font-medium text-[#2f5c96] hover:text-[#c19cca] transition-colors"
                      >
                        {mode === 'login' ? 'Forgot password?' : 'Back to Login'}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4 border border-red-200">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">{error}</h3>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Button
                      type="submit"
                      className="w-full justify-center py-3 text-lg shadow-lg shadow-[#2f5c96]/10"
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (mode === 'login' ? 'Sign in' : 'Send Reset Link')}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </Card>

          {/* 9. Alternative Action Section */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              New to the platform?{' '}
              <Link to="/register" className="font-semibold text-[#2f5c96] hover:text-[#c19cca] transition-colors">
                Create an account
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* 10. Footer (Minimal) */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-400">
            &copy; {new Date().getFullYear()} Indigo Beans Rooms. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4 text-sm text-gray-500">
            <a href="#" className="hover:text-gray-900">Privacy Policy</a>
            <a href="#" className="hover:text-gray-900">Terms of Service</a>
            <a href="#" className="hover:text-gray-900">Cookie Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
};